import React from 'react';
import { useLocation, useOutlet } from 'react-router-dom';
import { AppShell, rem } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { motion, useReducedMotion } from 'framer-motion';
import ShellHeader from '@/components/appshell/Shell.Header';
import ShellNavbar from '@/components/appshell/Shell.Navbar';
import ShellSpotlight from '@/components/appshell/Shell.Spotlight';
import useAppearance from '@/hooks/use.appearance';
import { ROOT_NODES } from '@/components/appshell/config/navigation.tree';
import { navigationResolve } from '@/function/navigation/navigation.find';
import { useIsCompact } from '@/hooks/use.responsive';
import { useAppDispatch, useAppSelector } from '@/store/store.hooks';
import { selectNavbarExpanded } from '@/store/ui/ui.selectors';
import { recordVisit, toggleNavbar } from '@/store/ui/ui.slice';
import { FADE_UP, SHELL_ENTER } from '@/theme/motion';
import { SHELL } from '@/theme/tokens';
import { useChatNotifications } from '@/hooks/use.chat.notifications';

const ShellLayout: React.FC = () => {
    const location = useLocation();
    const outlet = useOutlet();
    const dispatch = useAppDispatch();
    const reduced = useReducedMotion();
    const isCompact = useIsCompact();

    useAppearance();

    const desktopExpanded = useAppSelector(selectNavbarExpanded);
    const expanded = isCompact ? true : desktopExpanded;

    const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] = useDisclosure(false);

    const resolved = React.useMemo(() => navigationResolve(ROOT_NODES, location.pathname), [location.pathname]);

    const pageKey = resolved?.node.path ?? location.pathname;

    const chatUnread = useChatNotifications();

    React.useEffect(() => {
        const base = resolved ? `${resolved.node.label} · beDashboard` : 'beDashboard';
        document.title = chatUnread.total ? `(${chatUnread.total > 99 ? '99+' : chatUnread.total}) ${base}` : base;
    }, [resolved, chatUnread.total]);

    React.useEffect(() => {
        if (resolved) dispatch(recordVisit(resolved.node.path));
    }, [resolved, dispatch]);

    React.useEffect(() => { modals.closeAll(); }, [location.pathname]);

    return (
        <AppShell
            header={{ height: `calc(${rem(SHELL.headerHeight)} + env(safe-area-inset-top, 0px))` }}
            navbar={{
                width: expanded ? SHELL.navbarExpanded : SHELL.navbarCollapsed,
                breakpoint: 'md',
                collapsed: { mobile: !mobileOpened },
            }}
            padding={{ base: 'sm', sm: 'md', lg: 'lg' }}
            transitionDuration={reduced ? 0 : SHELL.navbarTransitionMs}
            transitionTimingFunction="cubic-bezier(0.22, 1, 0.36, 1)"
            styles={{
                navbar: {
                    transition: reduced ? 'none' : `width ${SHELL.navbarTransitionMs}ms cubic-bezier(0.22, 1, 0.36, 1)`,
                    backgroundColor: 'var(--bd-shell)',
                    borderColor: 'var(--bd-border)',
                },
                header: { backgroundColor: 'var(--bd-shell)', borderColor: 'var(--bd-border)', paddingTop: 'env(safe-area-inset-top, 0px)' },
            }}
        >
            <AppShell.Header>
                <motion.div variants={SHELL_ENTER.header} initial="hidden" animate="visible" style={{ height: '100%' }}>
                    <ShellHeader mobileOpened={mobileOpened} onMobileToggle={toggleMobile} />
                </motion.div>
            </AppShell.Header>

            <AppShell.Navbar zIndex={150}>
                <motion.div
                    variants={SHELL_ENTER.navbar}
                    initial="hidden"
                    animate="visible"
                    style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
                >
                    <ShellNavbar
                        expanded={expanded}
                        onToggle={() => dispatch(toggleNavbar())}
                        onNavigateMobile={closeMobile}
                        showToggle={!isCompact}
                    />
                </motion.div>
            </AppShell.Navbar>

            <AppShell.Main
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '100dvh',
                    paddingBottom: `calc(${rem(24)} + env(safe-area-inset-bottom, 0px))`,
                }}
            >
                <motion.div
                    variants={SHELL_ENTER.content}
                    initial="hidden"
                    animate="visible"
                    style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
                >
                    <motion.div
                        key={pageKey}
                        variants={FADE_UP}
                        initial={reduced ? false : 'hidden'}
                        animate="visible"
                        style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
                    >
                        {outlet}
                    </motion.div>
                </motion.div>
            </AppShell.Main>

            <ShellSpotlight />
        </AppShell>
    );
};

export default ShellLayout;
